import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Users,
  Clock,
  DollarSign,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/utils';

interface DashboardStats {
  pendingBookings: number;
  approvedBookings: number;
  totalTeams: number;
  monthlyRevenue: number;
}

interface RecentBooking {
  id: string;
  teamName: string;
  trackName: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  createdAt: string;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    pendingBookings: 0,
    approvedBookings: 0,
    totalTeams: 0,
    monthlyRevenue: 0,
  });
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const [statsRes, bookingsRes] = await Promise.all([
          api.get('/api/admin/stats'),
          api.get('/api/admin/bookings?limit=5&sort=createdAt:desc'),
        ]);

        if (statsRes) {
          setStats(statsRes);
        }
        if (bookingsRes.bookings) {
          setRecentBookings(bookingsRes.bookings);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-gray-400 mt-1">
          Overview of DriveIT NQ operations.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Pending Bookings</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {stats.pendingBookings}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-900/50 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-yellow-400" size={24} />
            </div>
          </div>
          <Link
            to="/admin/bookings?status=requested"
            className="text-sm text-yellow-400 hover:text-yellow-300 mt-3 flex items-center"
          >
            Review now <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Approved This Month</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {stats.approvedBookings}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-900/50 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-400" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Teams</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">
                {stats.totalTeams}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-900/50 rounded-lg flex items-center justify-center">
              <Users className="text-blue-400" size={24} />
            </div>
          </div>
          <Link
            to="/admin/teams"
            className="text-sm text-blue-400 hover:text-blue-300 mt-3 flex items-center"
          >
            View teams <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Monthly Revenue</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">
                {formatPrice(stats.monthlyRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-900/50 rounded-lg flex items-center justify-center">
              <DollarSign className="text-purple-400" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Recent Booking Requests</h2>
          <Link
            to="/admin/bookings"
            className="text-sm text-red-400 hover:text-red-300"
          >
            View all
          </Link>
        </div>

        {recentBookings.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="mx-auto text-gray-600 mb-3" size={40} />
            <p className="text-gray-400">No recent bookings.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {recentBookings.map((booking) => (
              <div key={booking.id} className="p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-white">{booking.teamName}</span>
                    <span className="text-gray-500">&bull;</span>
                    <span className="text-gray-400">{booking.trackName}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatDate(booking.bookingDate)} &bull; {booking.startTime} - {booking.endTime}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      booking.status === 'approved'
                        ? 'bg-green-900/50 text-green-400'
                        : booking.status === 'requested'
                        ? 'bg-yellow-900/50 text-yellow-400'
                        : booking.status === 'rejected'
                        ? 'bg-red-900/50 text-red-400'
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {booking.status}
                  </span>
                  {booking.status === 'requested' && (
                    <Link
                      to={`/admin/bookings?id=${booking.id}`}
                      className="btn btn-sm bg-gray-700 text-white hover:bg-gray-600"
                    >
                      Review
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/admin/bookings?status=requested"
          className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-yellow-600 transition-colors group"
        >
          <div className="w-12 h-12 bg-yellow-900/50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-yellow-900/70 transition-colors">
            <Clock className="text-yellow-400" size={24} />
          </div>
          <h3 className="font-semibold text-white mb-1">Review Bookings</h3>
          <p className="text-sm text-gray-400">
            Approve or reject pending booking requests.
          </p>
        </Link>

        <Link
          to="/admin/teams"
          className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-600 transition-colors group"
        >
          <div className="w-12 h-12 bg-blue-900/50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-900/70 transition-colors">
            <Users className="text-blue-400" size={24} />
          </div>
          <h3 className="font-semibold text-white mb-1">Manage Teams</h3>
          <p className="text-sm text-gray-400">
            View and manage registered teams.
          </p>
        </Link>

        <Link
          to="/admin/tracks"
          className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-green-600 transition-colors group"
        >
          <div className="w-12 h-12 bg-green-900/50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-900/70 transition-colors">
            <Calendar className="text-green-400" size={24} />
          </div>
          <h3 className="font-semibold text-white mb-1">Track Settings</h3>
          <p className="text-sm text-gray-400">
            Configure tracks and time slots.
          </p>
        </Link>
      </div>
    </div>
  );
}
